import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight, ChevronDown } from 'lucide-react';
import { navigationLinks } from '../data/mock';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8b2c67d7-f3f8-4dd8-ad33-921e8e7f1a96/artifacts/s6b7c1yc_transprant.png";

const hubLinks = [
  { name: 'Metabolic & Weight Health', href: '/hub/metabolic-weight-health' },
  { name: "Women's & Reproductive Health", href: '/hub/womens-reproductive-health' },
  { name: 'Pregnancy & Postpartum', href: '/hub/pregnancy-postpartum' },
  { name: 'Fitness & Body Transformation', href: '/hub/fitness-body-transformation' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hubOpen, setHubOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const dismissed = typeof window !== 'undefined' && sessionStorage.getItem('tcube_announcement_dismissed_v2');
    setBannerVisible(!dismissed);
  }, [location.pathname]);

  // Close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setHubOpen(false);
  }, [location.pathname]);

  // Body scroll lock + hide floating elements while mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.dataset.menuOpen = 'true';
    } else {
      document.body.style.overflow = '';
      delete document.body.dataset.menuOpen;
    }
    return () => {
      document.body.style.overflow = '';
      delete document.body.dataset.menuOpen;
    };
  }, [isMobileMenuOpen]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleHashNav = (e, href) => {
    setIsMobileMenuOpen(false);
    if (!href.includes('#')) return;
    const [path, hash] = href.split('#');
    if (location.pathname !== (path || '/')) {
      e.preventDefault();
      navigate(path || '/');
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          const offset = 100;
          window.scrollTo({ top: el.offsetTop - offset, behavior: 'smooth' });
        }
      }, 200);
    } else if (hash) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) {
        const offset = 100;
        window.scrollTo({ top: el.offsetTop - offset, behavior: 'smooth' });
      }
    }
  };

  const topOffset = bannerVisible ? 'top-9 sm:top-10' : 'top-0';
  const mainLinks = navigationLinks.slice(0, -1);
  const ctaLink = navigationLinks[navigationLinks.length - 1];

  return (
    <>
      <header
        className={`fixed left-0 right-0 z-[100] transition-all duration-300 ${topOffset} ${
          isScrolled
            ? 'bg-slate-900/95 backdrop-blur-md shadow-lg shadow-black/20 border-b border-slate-800'
            : 'bg-slate-900/70 backdrop-blur-sm'
        }`}
        data-testid="site-header"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <nav className="flex items-center justify-between h-16 sm:h-20" aria-label="Main navigation">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo-link">
              <img
                src={LOGO_URL}
                alt="TCUBE FITNESS Logo"
                className="h-12 sm:h-16 w-auto"
                width="160"
                height="64"
                decoding="async"
                fetchPriority="high"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              {mainLinks.map((link) =>
                link.href.includes('#') ? (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleHashNav(e, link.href)}
                    className="text-slate-300 font-medium hover:text-white transition-colors text-sm xl:text-base"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-slate-300 font-medium hover:text-white transition-colors text-sm xl:text-base"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )
              )}

              {/* Health Hubs dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setHubOpen(true)}
                onMouseLeave={() => setHubOpen(false)}
              >
                <button
                  onClick={() => setHubOpen((v) => !v)}
                  aria-expanded={hubOpen}
                  aria-haspopup="true"
                  className="flex items-center gap-1 text-slate-300 font-medium hover:text-white transition-colors text-sm xl:text-base"
                  data-testid="nav-health-hubs"
                >
                  Health Hubs
                  <ChevronDown className={`w-4 h-4 transition-transform ${hubOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                <div className={`absolute left-0 top-full pt-3 ${hubOpen ? 'block' : 'hidden'}`}>
                  <div className="w-64 bg-slate-900 border border-slate-800 rounded-xl shadow-xl shadow-black/40 p-2" role="menu">
                    {hubLinks.map((h) => (
                      <Link
                        key={h.href}
                        to={h.href}
                        onClick={() => setHubOpen(false)}
                        className="block px-3 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 text-sm transition-colors"
                        data-testid={`nav-hub-${h.href.split('/').pop()}`}
                        role="menuitem"
                      >
                        {h.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/book-consultation?from=nav-header"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-5 xl:px-6 py-2.5 rounded-full font-semibold hover:opacity-90 transition-all shadow-lg shadow-blue-500/25 text-sm xl:text-base whitespace-nowrap"
                data-testid="nav-book-consultation-btn"
              >
                Book Free Consultation
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-white relative z-[101]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav-drawer"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Drawer + Backdrop, rendered outside the header for proper stacking */}
      {/* Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-[9998] bg-slate-950/70 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
        data-testid="mobile-menu-backdrop"
      />

      {/* Drawer */}
      <aside
        id="mobile-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        data-testid="mobile-menu-drawer"
        className={`lg:hidden fixed top-0 right-0 bottom-0 z-[9999] w-[85vw] max-w-sm
          bg-[rgba(7,12,28,0.97)] backdrop-blur-xl
          border-l border-slate-800
          shadow-2xl shadow-black/60
          transform transition-transform duration-300 ease-out
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/80 flex-shrink-0">
          <Link
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3"
            data-testid="drawer-logo-link"
          >
            <img src={LOGO_URL} alt="TCUBE FITNESS Logo" className="h-10 w-auto" width="120" height="40" decoding="async" />
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close navigation menu"
            className="w-10 h-10 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white flex items-center justify-center transition-colors"
            data-testid="drawer-close-button"
          >
            <X size={22} />
          </button>
        </div>

        {/* Drawer links */}
        <nav className="flex-1 overflow-y-auto px-4 py-6" aria-label="Mobile primary">
          <ul className="flex flex-col gap-1.5">
            {mainLinks.map((link) => {
              const isActive =
                link.href === '/'
                  ? location.pathname === '/' && !link.href.includes('#')
                  : link.href.startsWith('/#')
                  ? false
                  : location.pathname.startsWith(link.href);
              const cls = `flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl text-base font-semibold transition-colors ${
                isActive
                  ? 'bg-blue-500/15 text-white border border-blue-500/30'
                  : 'text-slate-200 hover:bg-slate-800/80 active:bg-slate-800 border border-transparent'
              }`;
              return (
                <li key={link.name}>
                  {link.href.includes('#') ? (
                    <a
                      href={link.href}
                      onClick={(e) => handleHashNav(e, link.href)}
                      className={cls}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span>{link.name}</span>
                      <ArrowRight className="w-4 h-4 text-slate-500" aria-hidden="true" />
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cls}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span>{link.name}</span>
                      <ArrowRight className="w-4 h-4 text-slate-500" aria-hidden="true" />
                    </Link>
                  )}
                </li>
              );
            })}

            <li className="pt-3 mt-2 border-t border-slate-800/60">
              <p className="px-4 pb-1 text-xs uppercase tracking-wider text-slate-500 font-semibold">Health Hubs</p>
            </li>
            {hubLinks.map((h) => (
              <li key={h.href}>
                <Link
                  to={h.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-200 hover:bg-slate-800/80 active:bg-slate-800 border border-transparent transition-colors"
                  data-testid={`mobile-nav-hub-${h.href.split('/').pop()}`}
                >
                  <span>{h.name}</span>
                  <ArrowRight className="w-4 h-4 text-slate-500" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Drawer CTA footer */}
        <div className="px-5 pt-4 pb-6 border-t border-slate-800/80 flex-shrink-0">
          <Link
            to="/book-consultation?from=mobile-drawer"
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:opacity-95 text-white px-5 py-3.5 rounded-full font-bold text-base shadow-lg shadow-blue-500/30 transition-all"
            data-testid="drawer-cta-book"
          >
            {ctaLink.name}
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <p className="text-center text-slate-500 text-xs mt-3">
            Doctor-led · Online · Personalized
          </p>
        </div>
      </aside>
    </>
  );
};

export default Header;
